import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import {
  InvitationWithRelationsDto,
  ResponseDto,
  CreateInvitationDto,
} from "@mimber/schema"
import { invitationsKeys } from "../invitations.utils"

/**
 * Organization-specific hook for creating a new invitation
 * Can only be used in mimber-organization app
 * The organization context is automatically handled by ServerContext
 */
export const useCreateInvitation = (
  options?: UseMutationOptions<
    ResponseDto<InvitationWithRelationsDto>,
    Error,
    CreateInvitationDto
  >
): UseMutationResult<
  ResponseDto<InvitationWithRelationsDto>,
  Error,
  CreateInvitationDto
> => {
  guardAppUsage("mimber-organization", "useCreateInvitation")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (params: CreateInvitationDto) => {
      const response = await server.post<
        ResponseDto<InvitationWithRelationsDto>
      >("invitations", params)
      return response.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate all invitation lists to show the new invitation
      queryClient.invalidateQueries({ queryKey: invitationsKeys.findAll() })

      // Call user's onSuccess if provided
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
